import { Accordion, Button, Collapse, NumberInput, Select, Stack, Switch, TagsInput, TextInput, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { useMemo, type FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  tippecanoeFields,
  tippecanoeGroups,
  type FieldDescriptor,
  type TippecanoeGroup,
} from "../constants/tippecanoeFields";

export interface TippecanoeOptionsInputProps {
  basePath: string;
  isLoading: boolean;
  getInputProps: (name: string, opts?: { type?: "checkbox" | "input" }) => object;
}

interface FieldRendererProps {
  basePath: string;
  field: FieldDescriptor<TippecanoeGroup>;
  isLoading: boolean;
  getInputProps: TippecanoeOptionsInputProps["getInputProps"];
}

function FieldRenderer({ basePath, field, isLoading, getInputProps }: FieldRendererProps) {
  const path = `${basePath}.${field.key}`;

  switch (field.kind) {
    case "boolean":
      return (
        <Switch
          label={field.key}
          description={field.description}
          disabled={isLoading}
          {...getInputProps(path, { type: "checkbox" })}
        />
      );
    case "number":
      return (
        <NumberInput
          label={field.key}
          description={field.description}
          placeholder={field.placeholder ?? field.key}
          disabled={isLoading}
          min={field.min}
          max={field.max}
          {...getInputProps(path)}
        />
      );
    case "stringArray":
      return (
        <TagsInput
          label={field.key}
          description={field.description}
          placeholder={field.placeholder ?? field.key}
          disabled={isLoading}
          clearable
          {...getInputProps(path)}
        />
      );
    case "enum":
      return (
        <Select
          label={field.key}
          description={field.description}
          placeholder={field.placeholder ?? field.key}
          data={field.options ? [...field.options] : []}
          disabled={isLoading}
          clearable
          {...getInputProps(path)}
        />
      );
    case "string":
    default:
      return (
        <TextInput
          label={field.key}
          description={field.description}
          placeholder={field.placeholder ?? field.key}
          disabled={isLoading}
          {...getInputProps(path)}
        />
      );
  }
}

export const TippecanoeOptionsInput: FC<TippecanoeOptionsInputProps> = ({
  basePath,
  isLoading,
  getInputProps,
}) => {
  const intl = useIntl();
  const [opened, { toggle }] = useDisclosure(false);
  const fields = useMemo(() => tippecanoeFields(intl), [intl]);
  const groups = useMemo(() => tippecanoeGroups(intl), [intl]);

  const commonFields = fields.filter(f => f.group === "common");
  const advancedGroups = groups.filter(
    g => fields.some(f => f.group === g.group),
  );

  return (
    <Stack gap="xs">
      {commonFields.map(field => (
        <FieldRenderer
          key={field.key}
          basePath={basePath}
          field={field}
          isLoading={isLoading}
          getInputProps={getInputProps}
        />
      ))}

      <Button
        variant="subtle"
        color="gray"
        onClick={toggle}
        leftSection={opened ? <IconChevronDown size="1rem" /> : <IconChevronRight size="1rem" />}
        justify="flex-start"
        fullWidth
      >
        <FormattedMessage id="button.advancedOptions" defaultMessage="Advanced options" />
      </Button>

      <Collapse expanded={opened}>
        <Accordion chevronPosition="right" multiple>
          {advancedGroups.map((group) => {
            const groupFields = fields.filter(f => f.group === group.group);
            if (groupFields.length === 0) return null;

            return (
              <Accordion.Item key={group.group} value={group.group}>
                <Accordion.Control>
                  <Text fw={500}>{group.label}</Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="xs">
                    {groupFields.map(field => (
                      <FieldRenderer
                        key={field.key}
                        basePath={basePath}
                        field={field}
                        isLoading={isLoading}
                        getInputProps={getInputProps}
                      />
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </Collapse>
    </Stack>
  );
};
